import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllRestaurants, toggleRestaurantFeature, deleteRestaurantOrders, deleteFullRestaurantData, getAnalyticsEvents } from '../services/firebaseService'; // Added getAnalyticsEvents
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { trackAdminAction } from '../services/analyticsService'; // Import analytics function
import LoadingSpinner from '../components/loadingspinner'; // Import the loading spinner

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboardPage = () => {
    // Enhanced UI Styles
    const pageStyle = {
        padding: '30px',
        fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        backgroundColor: '#f4f6f8',
        minHeight: '100vh',
    };

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #e0e0e0',
    };

    const mainTitleStyle = {
        color: '#2c3e50',
        fontSize: '2.5rem',
        fontWeight: '600',
    };

    const logoutButtonStyle = {
        padding: '12px 20px',
        backgroundColor: '#e74c3c',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: '500',
        transition: 'background-color 0.3s ease',
    };

    const sectionTitleStyle = {
        color: '#34495e',
        borderBottom: '2px solid #3498db',
        paddingBottom: '10px',
        marginBottom: '25px',
        fontSize: '1.8rem',
        fontWeight: '500',
    };

    const chartContainerStyle = {
        marginBottom: '40px',
        padding: '25px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        maxWidth: '900px',
        margin: '0 auto 40px auto',
    };

    const searchInputStyle = {
        marginBottom: '25px',
        padding: '14px 18px',
        width: '100%',
        border: '1px solid #bdc3c7',
        borderRadius: '6px',
        boxSizing: 'border-box',
        fontSize: '1.1rem',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 10px', // Adds space between rows
        marginTop: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        overflow: 'hidden', // For rounded corners on table
    };

    const tableHeaderStyle = {
        padding: '15px 20px',
        textAlign: 'left',
        backgroundColor: '#3498db',
        color: 'white',
        fontSize: '1rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    };

    const tableCellStyle = {
        padding: '15px 20px',
        textAlign: 'left',
        color: '#34495e',
        borderBottom: '1px solid #ecf0f1', // Lighter border for rows
    };

    const buttonStyle = (isActive) => ({
        padding: '10px 15px',
        margin: '2px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        backgroundColor: isActive ? '#2ecc71' : '#f39c12',
        color: 'white',
        minWidth: '100px',
        fontWeight: '500',
        transition: 'background-color 0.3s ease, transform 0.2s ease',
        ':hover': {
            transform: 'translateY(-2px)',
        }
    });

    const actionButtonStyle = {
        padding: '10px 15px',
        margin: '4px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        color: 'white',
        fontSize: '0.95rem',
        fontWeight: '500',
        transition: 'background-color 0.3s ease, transform 0.2s ease',
        ':hover': {
            transform: 'translateY(-2px)',
        }
    };

    const dangerActionButtonStyle = {
        ...actionButtonStyle,
        backgroundColor: '#c0392b',
    };

    const destructiveActionButtonStyle = {
        ...actionButtonStyle,
        backgroundColor: '#96281b', // Darker red for more destructive
        marginLeft: '8px',
    };

    const loadingErrorContainerStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        textAlign: 'center',
        padding: '20px',
    };

    const loadingTextStyle = {
        fontSize: '1.5rem',
        color: '#34495e',
    };

    const errorTextStyle = {
        fontSize: '1.5rem',
        color: '#e74c3c',
        fontWeight: '500',
    };

    const noDataTextStyle = {
        textAlign: 'center',
        color: '#7f8c8d',
        fontSize: '1.1rem',
        padding: '20px 0',
    };


    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const isAdmin = localStorage.getItem('isAdminAuthenticated');
        if (isAdmin !== 'true') {
            navigate('/admin/login');
            return;
        }
        fetchRestaurants();
    }, [navigate]);

    const fetchRestaurants = async () => {
        setLoading(true);
        try {
            const data = await getAllRestaurants();
            const processedDataPromises = data.map(async (r) => {
                let usage = { userCount: 0 };
                try {
                    // Fetch all analytics events for this restaurant
                    const events = await getAnalyticsEvents(r.id);
                    // Filter for page_view events and count unique users
                    const pageViewEvents = events.filter(event => event.eventName === 'page_view' && event.eventData && event.eventData.userId);
                    const uniqueUserIds = new Set(pageViewEvents.map(event => event.eventData.userId));
                    usage.userCount = uniqueUserIds.size;

                } catch (analyticsError) {
                    console.error(`Failed to fetch or process analytics for restaurant ${r.id}:`, analyticsError);
                    // Keep default usage or set to error state if analytics fetch fails
                    usage = { userCount: 'N/A' }; 
                }
                return {
                    ...r,
                    onlineOrdering: r.onlineOrdering === undefined ? true : !!r.onlineOrdering,
                    tableOrdering: r.tableOrdering === undefined ? true : !!r.tableOrdering,
                    isActive: r.isActive === undefined ? true : !!r.isActive,
                    usage: usage
                };
            });
            const processedData = await Promise.all(processedDataPromises);
            setRestaurants(processedData);
            setError('');
        } catch (err) {
            setError('Failed to fetch restaurants. ' + err.message);
            console.error('Error fetching restaurants:', err);
            setRestaurants([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('isAdminAuthenticated');
        trackAdminAction('logout', { adminUser: 'current_admin_identifier' }); // Assuming you have a way to identify the admin
        navigate('/admin/login');
    };

    const handleToggleFeature = async (restaurantId, feature, currentValue) => {
        try {
            await toggleRestaurantFeature(restaurantId, feature, !currentValue);
            // Update local state immediately for better UX, or re-fetch
            setRestaurants(prev => 
                prev.map(r => r.id === restaurantId ? { ...r, [feature]: !r[feature] } : r)
            );
            trackAdminAction('toggle_feature', { restaurantId, feature, newValue: !currentValue });
            // Or uncomment to refetch all: await fetchRestaurants();
        } catch (err) {
            setError(`Failed to toggle ${feature} for ${restaurantId}. ` + err.message);
            console.error(`Error toggling ${feature}:`, err);
        }
    };

    const handleDeleteData = async (restaurantId, dataType) => {
        let confirmMessage = '';
        let action;

        if (dataType === 'orders') {
            confirmMessage = `Are you sure you want to delete ALL ORDERS for restaurant ${restaurantId}? This action cannot be undone.`;
            action = () => deleteRestaurantOrders(restaurantId);
        } else if (dataType === 'fullRestaurant') {
            confirmMessage = `Are you sure you want to delete ALL DATA (including menu, settings, etc.) for restaurant ${restaurantId}? This is highly destructive and cannot be undone.`;
            action = () => deleteFullRestaurantData(restaurantId); // This is a placeholder and will throw an error as per its implementation
        } else {
            console.warn('Unknown data type for deletion:', dataType);
            setError('Unknown data type for deletion.');
            return;
        }

        if (window.confirm(confirmMessage)) {
            try {
                setLoading(true);
                await action();
                trackAdminAction(dataType === 'orders' ? 'delete_orders' : 'delete_full_restaurant', { restaurantId });
                // After deletion, refresh the list of restaurants
                // For full restaurant deletion, the restaurant would be gone
                if (dataType === 'fullRestaurant') {
                    setRestaurants(prev => prev.filter(r => r.id !== restaurantId));
                } else {
                    // For order deletion, we might just refresh or update usage stats if applicable
                    // For now, just log success or refetch all
                    console.log(`${dataType} deleted for ${restaurantId}`);
                    // Potentially update usage stats here if they were live
                }
                // await fetchRestaurants(); // Or selectively update UI
            } catch (err) {
                setError(`Failed to delete ${dataType} for ${restaurantId}. ` + err.message);
                console.error(`Error deleting ${dataType}:`, err);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <div style={loadingErrorContainerStyle}><p style={errorTextStyle}>{error}</p></div>;

    return (
        <div style={pageStyle}>
            <div style={headerStyle}>
                <h1 style={mainTitleStyle}>Admin Dashboard</h1>
                <button onClick={handleLogout} style={logoutButtonStyle} 
                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#c0392b'} 
                        onMouseOut={e => e.currentTarget.style.backgroundColor = '#e74c3c'}>
                    Logout
                </button>
            </div>

            <h2 style={sectionTitleStyle}>Analytics Overview</h2>
            {restaurants.length > 0 ? (
                <div style={chartContainerStyle}>
                    <Bar
                        data={{
                            labels: restaurants.map(r => r.name),
                            datasets: [
                                {
                                    label: 'User Count',
                                    data: restaurants.map(r => typeof r.usage?.userCount === 'number' ? r.usage.userCount : 0),
                                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                                    borderColor: 'rgba(54, 162, 235, 1)',
                                    borderWidth: 1,
                                },
                            ],
                        }}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: { position: 'top' },
                                title: { display: true, text: 'Restaurant Performance' },
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    ticks: {
                                        callback: function(value) {
                                            // Display integers for user count
                                            if (Number.isInteger(value)) {
                                                return value;
                                            }
                                            return null; // Hide non-integer ticks if any
                                        },
                                        stepSize: 1 // Ensure ticks are integers
                                    }
                                }
                            }
                        }}
                    />
                </div>
            ) : (
                <p style={noDataTextStyle}>No data available for charts.</p>
            )}

            <h2 style={sectionTitleStyle}>Restaurant Management</h2>
            <input 
                type="text" 
                placeholder="Search restaurants..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                style={searchInputStyle}
                onFocus={e => {e.currentTarget.style.borderColor = '#3498db'; e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(52,152,219,.25), inset 0 1px 3px rgba(0,0,0,0.06)';}}
                onBlur={e => {e.currentTarget.style.borderColor = '#bdc3c7'; e.currentTarget.style.boxShadow = 'inset 0 1px 3px rgba(0,0,0,0.06)';}}
            />
            {restaurants.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && <p style={noDataTextStyle}>No restaurants found.</p>}
            <table style={tableStyle}>
                <thead>
                    <tr style={{ backgroundColor: '#f0f2f5' }}>
                        <th style={tableHeaderStyle}>Name</th>
                        <th style={tableHeaderStyle}>Online Ordering</th>
                        <th style={tableHeaderStyle}>Table Ordering</th>
                        <th style={tableHeaderStyle}>Overall Access</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {restaurants.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase())).map(restaurant => (
                        <tr key={restaurant.id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={tableCellStyle}>{restaurant.name}</td>
                            <td style={tableCellStyle}>
                                <button onClick={() => handleToggleFeature(restaurant.id, 'onlineOrdering', restaurant.onlineOrdering)} style={buttonStyle(restaurant.onlineOrdering)}>
                                    {restaurant.onlineOrdering ? 'Disable' : 'Enable'}
                                </button>
                            </td>
                            <td style={tableCellStyle}>
                                <button onClick={() => handleToggleFeature(restaurant.id, 'tableOrdering', restaurant.tableOrdering)} style={buttonStyle(restaurant.tableOrdering)}>
                                    {restaurant.tableOrdering ? 'Disable' : 'Enable'}
                                </button>
                            </td>
                            <td style={tableCellStyle}>
                                <button onClick={() => handleToggleFeature(restaurant.id, 'isActive', restaurant.isActive)} style={buttonStyle(restaurant.isActive)}>
                                    {restaurant.isActive ? 'Disable Access' : 'Enable Access'}
                                </button>
                            </td>
                            <td style={tableCellStyle}>
                                <button 
                                    onClick={() => handleDeleteData(restaurant.id, 'orders')} 
                                    style={dangerActionButtonStyle}
                                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#a52a20'}
                                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#c0392b'}>
                                    Delete All Orders
                                </button>
                                <button 
                                    onClick={() => handleDeleteData(restaurant.id, 'fullRestaurant')} 
                                    style={destructiveActionButtonStyle}
                                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#7d2115'}
                                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#96281b'}>
                                    Delete Full Restaurant
                                </button>
                                {/* Add more granular controls as needed */}                            
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Removed old style definitions as they are now part of the component's style objects
// const tableHeaderStyle = { ... };
// const tableCellStyle = { ... };
// const buttonStyle = (isActive) => ({ ... });
// const actionButtonStyle = { ... };










export default AdminDashboardPage;